import { Card } from "@dune/ui";
import {
	WEBHOOK_EVENT_DETAILS,
	WEBHOOK_EVENT_VALUES,
	type WebhookEvent,
} from "@dune/types";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface SchemaField {
	name: string;
	description: string;
	type: string;
}

interface ZodInternals {
	def?: {
		type?: string;
		values?: unknown[];
	};
	description?: string;
}

interface ZodObjectWithShape {
	shape: Record<string, unknown>;
}

function hasShape(value: unknown): value is ZodObjectWithShape {
	return (
		typeof value === "object" &&
		value !== null &&
		"shape" in value &&
		typeof (value as ZodObjectWithShape).shape === "object"
	);
}

function getZodTypeName(schema: unknown): string {
	const internals = schema as ZodInternals;
	const def = internals.def;

	if (!def?.type) return "unknown";
	switch (def.type) {
		case "literal": {
			const value = def.values?.at(0);
			return typeof value === "string" ? `"${value}"` : String(value);
		}
		case "number":
			return "number";
		case "string":
			return "string";
		case "boolean":
			return "boolean";
		case "array":
			return "array";
		case "object":
			return "object";
		case "date":
			return "date";
		default:
			return def.type;
	}
}

function extractSchemaFields(schema: unknown): SchemaField[] {
	if (!hasShape(schema)) return [];

	return Object.entries(schema.shape).map(([name, fieldSchema]) => {
		const internals = fieldSchema as ZodInternals;
		return {
			name,
			type: getZodTypeName(fieldSchema),
			description: internals.description ?? "",
		};
	});
}

export const WebhookEventsTab = () => {
	const [expandedEvent, setExpandedEvent] = useState<WebhookEvent | null>(
		"NEW_BROKER",
	);

	const toggleEvent = (event: WebhookEvent): void => {
		setExpandedEvent(expandedEvent === event ? null : event);
	};

	const getSchemaFields = (event: WebhookEvent): SchemaField[] => {
		const schema = WEBHOOK_EVENT_DETAILS[event].schema;
		return extractSchemaFields(schema);
	};

	return (
		<div className="space-y-4">
			<div className="mb-6">
				<p className="text-sm text-muted-foreground">
					Consulter la liste des types d'évènements disponibles et le schema du
					payload envoyé pour chacun.
				</p>
			</div>

			{WEBHOOK_EVENT_VALUES.map((event) => {
				const details = WEBHOOK_EVENT_DETAILS[event];
				const isExpanded = expandedEvent === event;
				const fields = getSchemaFields(event);

				return (
					<Card key={event} className="overflow-hidden">
						<button
							onClick={() => toggleEvent(event)}
							type="button"
							className="w-full flex items-start justify-between gap-4 p-6 cursor-pointer hover:bg-muted/50 transition-colors text-left"
						>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-base mb-1">
									{details.label}
								</h3>
								<p className="text-sm text-muted-foreground">
									{details.description}
								</p>
							</div>
							<div className="flex-shrink-0 mt-1">
								{isExpanded ? (
									<ChevronUp className="h-5 w-5 text-muted-foreground" />
								) : (
									<ChevronDown className="h-5 w-5 text-muted-foreground" />
								)}
							</div>
						</button>

						{isExpanded && (
							<div className="border-t px-6 py-4 bg-muted/30 space-y-4">
								<div>
									<h4 className="font-medium text-sm mb-3">
										Champs du payload
									</h4>
									<div className="space-y-3">
										{fields.length > 0 ? (
											fields.map((field) => (
												<div
													key={field.name}
													className="border-l-2 border-primary/30 pl-3"
												>
													<div className="flex items-center gap-2 mb-1">
														<code className="text-xs font-mono bg-muted px-2 py-1 rounded">
															{field.name}
														</code>
														<span className="text-xs text-muted-foreground italic">
															{field.type}
														</span>
													</div>
													{field.description && (
														<p className="text-xs text-muted-foreground">
															{field.description}
														</p>
													)}
												</div>
											))
										) : (
											<p className="text-xs text-muted-foreground">
												Aucun champ
											</p>
										)}
									</div>
								</div>

								<div>
									<h4 className="font-medium text-sm mb-3">
										Exemple de payload
									</h4>
									<div className="bg-muted rounded-lg p-3 overflow-x-auto">
										<pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
											{JSON.stringify(details.example, null, 2)}
										</pre>
									</div>
								</div>
							</div>
						)}
					</Card>
				);
			})}
		</div>
	);
};
